import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/styles";
import { uploadDocument } from "../store/slices/applicationFormSlice";
import { documentNames, identityPreviewDocumentNames } from "src/constants";
import { hasDriverLicenceDocument } from "src/utils/commonFunctions";
import Document from "./Document";

const Input = styled("input")({
  display: "none",
});

const LOG_PREFIX = "[Identity]";
const STATUS_RESET_DELAY = 6000;

const UPLOAD_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const STATUS_BUTTON = {
  [UPLOAD_STATUS.IDLE]: { color: "secondary" },
  [UPLOAD_STATUS.LOADING]: { color: "secondary" },
  [UPLOAD_STATUS.SUCCESS]: { color: "success", text: "Upload successful" },
  [UPLOAD_STATUS.ERROR]: { color: "error", text: "Upload failed" },
};

// `key` doubles as the required-item key in Notes.js, so the progress tooltip can scroll to `${key}_${customerId}`
const IDENTITY_UPLOADS = [
  {
    key: "driverLicence",
    documentName: documentNames.DRIVER_LICENCE,
    label: "Upload Driver Licence",
  },
  {
    key: "IDDocument",
    documentName: documentNames.ID_DOCUMENT,
    label: "Upload ID",
  },
];

export default function Identity({ customer, applicationId }) {
  const dispatch = useDispatch();
  const [uploadStatus, setUploadStatus] = useState({});
  const resetTimers = useRef({});
  const isMounted = useRef(true);

  const customerId = customer?._id;
  const hasDriverLicence = hasDriverLicenceDocument(customer);
  const identityDocuments =
    customer?.documents?.filter((doc) =>
      identityPreviewDocumentNames.includes(doc?.name)
    ) ?? [];

  useEffect(() => {
    const timers = resetTimers.current;
    return () => {
      isMounted.current = false;
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const updateStatus = useCallback((key, status) => {
    if (!isMounted.current) return;

    clearTimeout(resetTimers.current[key]);
    setUploadStatus((prev) => ({ ...prev, [key]: status }));

    if (status === UPLOAD_STATUS.SUCCESS || status === UPLOAD_STATUS.ERROR) {
      resetTimers.current[key] = setTimeout(() => {
        setUploadStatus((prev) => ({ ...prev, [key]: UPLOAD_STATUS.IDLE }));
      }, STATUS_RESET_DELAY);
    }
  }, []);

  const handleUpload = async (event, { key, documentName }) => {
    const [file] = event.target.files ?? [];
    // Reset so selecting the same file again still fires onChange
    event.target.value = "";

    if (!file) return;

    // File name is deliberately not logged as it often contains the customer's name
    const logContext = {
      documentName,
      customerId,
      applicationId,
      fileType: file.type || "unknown",
      fileSizeKB: Math.round(file.size / 1024),
    };

    if (!customerId || !applicationId) {
      console.error(`${LOG_PREFIX} Upload skipped, missing customerId or applicationId`, logContext);
      updateStatus(key, UPLOAD_STATUS.ERROR);
      return;
    }

    console.info(`${LOG_PREFIX} Upload started`, logContext);
    updateStatus(key, UPLOAD_STATUS.LOADING);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("applicationId", applicationId);
    formData.append("customerId", customerId);
    formData.append("name", documentName);

    const res = await dispatch(uploadDocument(formData));

    if (res?.error) {
      console.error(`${LOG_PREFIX} Upload failed`, {
        ...logContext,
        reason: res.payload?.message || res.error?.message || "unknown",
      });
      updateStatus(key, UPLOAD_STATUS.ERROR);
      return;
    }

    console.info(`${LOG_PREFIX} Upload succeeded`, logContext);
    updateStatus(key, UPLOAD_STATUS.SUCCESS);
  };

  return (
    <Grid container item xs={12} spacing={0} style={{ marginBottom: "30px" }}>
      <Grid item xs={12}>
        <Typography gutterBottom>Identity</Typography>
      </Grid>
      <Grid item xs={12}>
        {identityDocuments.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            style={{ marginBottom: "20px" }}
          >
            {identityDocuments.map((doc) => (
              <Document key={doc._id} document={doc} />
            ))}
          </Stack>
        )}
        <Stack
          spacing={1}
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
        >
          {IDENTITY_UPLOADS.map((upload) => {
            const status = uploadStatus[upload.key] ?? UPLOAD_STATUS.IDLE;
            const { color, text } = STATUS_BUTTON[status];
            const inputId = `upload-${upload.key}-${customerId}`;

            return (
              <label
                key={upload.key}
                id={`${upload.key}_${customerId}`}
                htmlFor={inputId}
              >
                <Input
                  id={inputId}
                  type="file"
                  onChange={(event) => handleUpload(event, upload)}
                />
                <LoadingButton
                  loading={status === UPLOAD_STATUS.LOADING}
                  variant="contained"
                  component="span"
                  color={color}
                >
                  {text ?? upload.label}
                </LoadingButton>
              </label>
            );
          })}
        </Stack>
        {!hasDriverLicence && (
          <Typography
            variant="caption"
            display="block"
            color="error"
            style={{ marginTop: "5px" }}
          >
            Driver licence is required.
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}
